import { PaneData } from '@toggle/chart';
import { Button, DropdownProps } from '@toggle/design-system';
import clsx from 'clsx';
import React, { ReactNode } from 'react';

import { RangeHorizon, ResampleIntervals } from '~/types/resample.types';
import type { SeriesType } from '~/types/series.types';

import {
  ChartTypeSelector,
  ChartTypeSelectorProps,
} from '../chart-type-selector/ChartTypeSelector';
import {
  ChipHorizonSelector,
  ChipHorizonSelectorLocales,
} from '../chip-horizon-selector/ChipHorizonSelector';
import {
  ResampleOption,
  ResampleSelector,
  ResampleSelectorLocales,
} from '../resample-selector/ResampleSelector';
import styles from './ViewController.module.scss';

export interface ViewControllerLocales {
  clearAllCharts: string;
  horizons: ChipHorizonSelectorLocales;
  resamples: ResampleSelectorLocales;
}

export interface ViewControllerProps {
  enabledResamples: ResampleIntervals[];
  horizonRanges: RangeHorizon[];
  selectedRange: RangeHorizon | null;
  selectedResample: ResampleIntervals;
  selectedSeriesType?: SeriesType;
  paneData?: PaneData[];
  onRangeChange: (range: RangeHorizon) => void;
  onResampleChange: (resample: ResampleIntervals) => void;
  onChartTypeChange?: ChartTypeSelectorProps['onChange'];
  onItemsShowChange: DropdownProps<ResampleOption>['onItemsShowChange'];
  isResampleDropdownOpen: boolean;
  disableMenuBar: boolean;
  disable1DHorizon: boolean;
  hasOHLC?: boolean;
  isSimpleChart?: boolean;
  renderFullScreenButton?: () => ReactNode;
  clearAllCharts?: () => void;
  maxHorizon?: RangeHorizon;
  locales: ViewControllerLocales;
  showChartTypeSelector?: boolean;
}

export const ViewController = ({
  selectedRange,
  selectedResample,
  paneData,
  onRangeChange,
  onResampleChange,
  onChartTypeChange,
  horizonRanges,
  disable1DHorizon,
  isResampleDropdownOpen,
  disableMenuBar,
  hasOHLC,
  onItemsShowChange,
  enabledResamples,
  isSimpleChart = false,
  showChartTypeSelector = true,
  renderFullScreenButton,
  clearAllCharts,
  maxHorizon,
  locales,
}: ViewControllerProps) => {
  const disabledRange = disable1DHorizon ? [horizonRanges[0]] : [];
  const disabledHorizons = disableMenuBar ? horizonRanges : disabledRange;

  const maxHorizonIndex =
    isSimpleChart && maxHorizon
      ? horizonRanges.findIndex(h => h === maxHorizon)
      : horizonRanges.length - 1;
  const filteredHorizonsRanged = horizonRanges.slice(0, maxHorizonIndex + 1);
  const isEmptyChart = !paneData?.[0].chartAssetData.length;

  return (
    <div
      className={clsx(styles.viewControllerRoot, {
        [styles.isSimpleChart]: isSimpleChart,
      })}
      data-testid="view-controller"
    >
      {horizonRanges && (
        <ChipHorizonSelector
          horizonRanges={filteredHorizonsRanged}
          selectedRange={selectedRange}
          onChange={onRangeChange}
          disabledHorizons={disabledHorizons}
          locales={locales.horizons}
        />
      )}

      {!isSimpleChart && (
        <ResampleSelector
          selectedResample={selectedResample}
          onChange={onResampleChange}
          enabledResamples={enabledResamples}
          isResampleDropdownOpen={isResampleDropdownOpen}
          disableMenuBar={disableMenuBar}
          onItemsShowChange={onItemsShowChange}
          locales={locales.resamples}
        />
      )}

      {onChartTypeChange && showChartTypeSelector && (
        <div className={styles.flexWrapper}>
          <ChartTypeSelector
            selectedSeriesType={paneData?.[0].seriesType}
            paneData={paneData}
            onChange={onChartTypeChange}
            disableMenuBar={disableMenuBar}
            hasOHLC={hasOHLC}
            selectedResample={selectedResample}
          />
          {renderFullScreenButton?.()}
        </div>
      )}
      {!isSimpleChart && !isEmptyChart && clearAllCharts && (
        <Button
          className={styles.clearAllChartsButton}
          data-testid="clear-all-charts-button"
          label={locales.clearAllCharts}
          onClick={clearAllCharts}
          variant="empty"
          size="small"
          iconName="Clear"
        />
      )}
    </div>
  );
};
